/**
 * Sanitizes user input to prevent XSS attacks and dangerous content
 * Removes script tags, javascript: protocols, and dangerous HTML
 */

/**
 * Sanitizes a text input value by removing dangerous patterns
 * This is a lightweight sanitization for form inputs
 */
export function sanitizeTextInput(value: string): string {
  if (!value) return value;

  return value
    // Remove script tags
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove javascript: protocols
    .replace(/javascript:/gi, '')
    // Remove data: protocols (can be used for XSS)
    .replace(/data:text\/html/gi, '')
    // Remove on* event handlers
    .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '')
    // Remove HTML tags (keep only text)
    .replace(/<[^>]*>/g, '')
    // Remove HTML entities that could be dangerous
    .replace(/&lt;script/gi, '')
    .replace(/&lt;iframe/gi, '')
    .trim();
}

/**
 * Validates if input contains dangerous patterns
 */
export function hasDangerousInput(value: string): boolean {
  if (!value) return false;

  const dangerousPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /data:text\/html/gi,
    /\s*on\w+\s*=\s*["'][^"']*["']/gi,
    /<[^>]*>/g, // Any HTML tags
    /&lt;script/gi,
    /&lt;iframe/gi,
  ];

  return dangerousPatterns.some(pattern => pattern.test(value));
}

/**
 * Validates SAM ID format (alphanumeric, underscores, dots - no hyphens or special characters)
 */
export function isValidSamId(value: string): boolean {
  if (!value) return false;
  
  // SAM ID should only contain alphanumeric characters, underscores, and dots
  // No HTML tags, scripts, hyphens, or special characters that could be dangerous
  const samIdPattern = /^[a-zA-Z0-9._]+$/;
  
  return samIdPattern.test(value) && !hasDangerousInput(value);
}

/**
 * Gets a user-friendly error message for invalid input
 */
export function getInputErrorMessage(value: string, fieldName: string = 'field'): string | null {
  if (!value) return null;
  
  if (hasDangerousInput(value)) {
    return `${fieldName} contains invalid characters. Only letters, numbers, dots, and underscores are allowed.`;
  }
  
  return null;
}

